import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { endOfMonth, format, isSameDay, startOfDay, startOfMonth } from 'date-fns';
import datePickerCalendarStyles from 'common/theme/fields/date-picker/datePickerCalendarStyles';
import userApi from 'api/graphql/user';

const dayKey = (date) => format(date, 'yyyy-MM-dd');

/**
 * Calendario interactivo para programar la MISMA sesión en varias fechas.
 * Consciente de DISPONIBILIDAD: consulta `availableDays` (categoría + idioma + hora)
 * para el mes visible y solo habilita los días con al menos un entrenador disponible.
 * - La fecha primaria (la del formulario) se resalta en dorado sólido.
 * - Las fechas adicionales seleccionadas se marcan con borde dorado.
 * - `disabled`: se muestra atenuado hasta completar cliente/plan + categoría + idioma + hora.
 * Reporta al padre el mapa { 'yyyy-MM-dd': trainer_ids } vía onAvailabilityChange
 * (para asignar un entrenador disponible a cada fecha al crear).
 */
function SessionDatesCalendar({
  primaryDate = null,
  extraDates,
  onToggle,
  sessionTypeId = null,
  language = '',
  fromHour = null,
  toHour = null,
  trainerId = null,
  takenDates = null,
  disabled = false,
  availabilityKey = 'primary',
  onAvailabilityChange = null
}) {
  const theme = useTheme();
  const { t } = useTranslation(['sessions']);
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(new Date()));
  const [availability, setAvailability] = useState(null); // { 'yyyy-MM-dd': [trainerIds] }
  const [loading, setLoading] = useState(false);

  const canQuery = !disabled
    && Boolean(sessionTypeId)
    && Boolean(language)
    && Number.isInteger(fromHour)
    && Number.isInteger(toHour);

  useEffect(() => {
    if (!canQuery) {
      setAvailability(null);
      onAvailabilityChange?.(availabilityKey, null);
      return undefined;
    }

    let active = true;
    setLoading(true);
    userApi
      .availableDays({
        startDate: format(startOfMonth(visibleMonth), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(visibleMonth), 'yyyy-MM-dd'),
        sessionTypeId,
        language,
        fromHour,
        toHour,
        trainerId,
      })
      .then(({ data }) => {
        if (!active) return;
        const map = {};
        (data?.availableDays || []).forEach((d) => {
          map[d.date] = d.trainer_ids || [];
        });
        setAvailability(map);
        onAvailabilityChange?.(availabilityKey, map);
      })
      .catch(() => {
        if (!active) return;
        // Ante error de red no bloqueamos el calendario (se valida igual al crear).
        setAvailability(null);
        onAvailabilityChange?.(availabilityKey, null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [canQuery, sessionTypeId, language, fromHour, toHour, trainerId, visibleMonth, availabilityKey, onAvailabilityChange]);

  const shouldDisableDate = useCallback(
    (day) => {
      // Fechas YA agendadas a esta hora en otros horarios del lote: deshabilitadas
      // (el cliente no puede estar en dos sesiones a la misma hora).
      if (takenDates && takenDates.has(dayKey(day))) return true;
      // Sin datos de disponibilidad (aún cargando o error) no deshabilitamos nada.
      if (!availability) return false;
      // La fecha primaria viene validada por el formulario: se deja visible.
      if (primaryDate && isSameDay(day, primaryDate)) return false;
      return !(dayKey(day) in availability);
    },
    [availability, primaryDate, takenDates]
  );

  const renderDay = useCallback(
    (dayProps) => {
      const { day, ...other } = dayProps;
      const isPrimary = primaryDate && isSameDay(day, primaryDate);
      const isExtra = extraDates.some((d) => isSameDay(d, day));

      return (
        <PickersDay
          {...other}
          day={day}
          sx={{
            ...(isExtra && !isPrimary && {
              backgroundColor: 'gold.soft',
              border: '1px solid',
              borderColor: 'gold.main',
              color: 'gold.main',
              fontWeight: 800
            }),
            ...(isPrimary && {
              backgroundColor: 'gold.main !important',
              color: '#111418 !important',
              fontWeight: 800
            })
          }}
        />
      );
    },
    [primaryDate, extraDates]
  );

  const calendarSx = useMemo(
    () => ({
      ...datePickerCalendarStyles(theme),
      position: 'relative',
      borderRadius: '14px',
      border: '1px solid',
      borderColor: 'hairline.main',
      backgroundColor: 'surface.content',
      '& .MuiDateCalendar-root': { width: '100%', maxHeight: 'none' },
      // Números de día / labels / header legibles en claro (antes tenues).
      '& .MuiPickersCalendarHeader-label': { color: theme.palette.text.primary },
      '& .MuiDayCalendar-weekDayLabel': { color: theme.palette.text.secondary },
      '& .MuiPickersDay-root': { color: theme.palette.text.primary },
      '& .MuiPickersDay-root.Mui-disabled': { color: theme.palette.text.disabled },
      ...(disabled && { opacity: 0.45, pointerEvents: 'none' })
    }),
    [theme, disabled]
  );

  return (
    <Box sx={calendarSx} aria-disabled={disabled}>
      <DateCalendar
        value={null}
        disablePast
        shouldDisableDate={shouldDisableDate}
        onChange={(d) => d && onToggle(startOfDay(d))}
        onMonthChange={(m) => setVisibleMonth(startOfMonth(m))}
        slots={{ day: renderDay }}
      />
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.35)',
            borderRadius: '14px',
          }}>
          <CircularProgress size={22} sx={{ color: 'gold.main' }} />
        </Box>
      )}
      {!disabled && canQuery && availability && Object.keys(availability).length === 0 && !loading && (
        <Typography variant="caption" sx={{ display: 'block', px: 2, pb: 1.5, color: 'text.secondary' }}>
          {t('noAvailableDays', {
            ns: 'sessions',
            defaultValue: 'No hay días con disponibilidad para este horario en el mes visible.',
          })}
        </Typography>
      )}
    </Box>
  );
}

SessionDatesCalendar.propTypes = {
  primaryDate: PropTypes.instanceOf(Date),
  extraDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)).isRequired,
  onToggle: PropTypes.func.isRequired,
  sessionTypeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  language: PropTypes.string,
  fromHour: PropTypes.number,
  toHour: PropTypes.number,
  trainerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  takenDates: PropTypes.instanceOf(Set),
  disabled: PropTypes.bool,
  availabilityKey: PropTypes.string,
  onAvailabilityChange: PropTypes.func,
};

export default SessionDatesCalendar;
